import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  Info,
  MessageCircle,
  Phone,
  Minus,
  Plus,
  Sparkles,
  Monitor,
  Stethoscope,
  CheckCircle2,
} from "lucide-react";

const PAGE_PATH = "/earlobe-repair-surgery-mumbai";
const PAGE_URL = `https://drvickyjain.com${PAGE_PATH}`;
const COSMETIC_PATH = "/cosmetic-surgery-in-mumbai";
const OTOPLASTY_PATH = "/otoplasty-ear-reshaping-surgery-mumbai";
const MOLE_PATH = "/mole-removal-mumbai";
const KELOID_PATH = "/keloid-treatment-mumbai";

const EARLOBE_FAQS = [
  {
    question: "Is earlobe repair painful?",
    answer:
      "No. The procedure is performed under local anesthesia. The anesthesia injection into the earlobe produces a brief sting that resolves within seconds. The repair itself is painless. Post-procedure mild tenderness for 1 to 2 days is managed with standard pain relief and is minimal for most patients.",
  },
  {
    question: "Will there be a visible scar after earlobe repair?",
    answer:
      "A fine linear scar remains at the repair site. In most cases this fades to near-invisible within 6 to 12 months. The earlobe is a small area with good wound healing characteristics and scar visibility is rarely a concern for patients after the initial healing period. Proper suture technique, layered closure, and fine suture material all contribute to the minimal final scar.",
  },
  {
    question: "Can both earlobes be repaired in the same session?",
    answer:
      "Yes. Both earlobes can be repaired in a single session of 30 to 60 minutes under local anesthesia. There is no clinical reason to separate them into two procedures and most patients prefer to address both in one visit.",
  },
  {
    question: "When can I wear earrings again after repair?",
    answer:
      "Re-piercing is appropriate 6 to 8 weeks after repair once the tissue has healed and regained structural strength. The new piercing should be placed at a different position with adequate tissue margin from the repair line. Wearing lightweight earrings initially after re-piercing and avoiding very heavy earrings long-term reduces the risk of future tears.",
  },
  {
    question: "Can a completely split earlobe be repaired?",
    answer:
      "Yes. Complete splits are one of the most common presentations for earlobe repair. The two halves of the split earlobe are freshened along their wound edges and closed in layers to reunite them. The repair produces a structurally sound earlobe with a fine scar line that fades significantly over time.",
  },
  {
    question: "How long does earlobe repair take?",
    answer:
      "20 to 40 minutes for straightforward repairs. Complex repairs including gauge reconstructions or multiple piercing deformities may take slightly longer. The procedure is performed in the clinic with no hospital admission required and patients leave immediately after.",
  },
  {
    question: "Can stretched earlobes from gauging be repaired?",
    answer:
      "Yes. Gauge repair is more complex than standard split repair because the defect is larger and requires tissue reconstruction rather than simple edge-to-edge closure. The approach depends on the size of the gauge used. Smaller gauges can often be closed with local tissue advancement. Very large gauges may require staged repair or more extensive reconstruction. Dr. Jain assesses the gauge defect during consultation and discusses what is achievable.",
  },
  {
    question: "Will the earlobe tear again after repair?",
    answer:
      "Re-tearing is possible but uncommon when precautions are followed. The repair is structurally sound when healing is complete. Re-tearing most commonly occurs when heavy earrings are resumed too early after repair, when the new piercing is placed too close to the repair site, or when earrings are left in during sleep. Following the recommended precautions and choosing lightweight earrings after re-piercing significantly reduces the risk of a repeat tear.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Earlobe repair (lobuloplasty)",
      description:
        "Minor outpatient repair of torn, split, elongated, or gauged earlobes under local anesthesia with edge freshening and layered closure for durable healing and optional later re-piercing.",
      procedureType: "Surgical",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Earlobe" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: EARLOBE_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function EarlobeJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: COSMETIC_PATH },
    { name: "Earlobe Repair", href: PAGE_PATH },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic · Ear</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">Earlobe Repair Surgery</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Lobuloplasty for Torn, Split &amp; Stretched Earlobes by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Earlobe Repair" icon="👂" size="hero"  imageSrc={HERO.earlobeRepair} imageAlt="Earlobe repair"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Earlobe Repair Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Earlobe repair, clinically called lobuloplasty, is a minor surgical procedure that corrects torn, split, elongated, or stretched earlobes to restore their natural shape and structural integrity. The procedure is performed under local anesthesia, takes 20 to 40 minutes, and requires no hospital admission. Most patients return to normal activity the same day. After full healing, the earlobe can be re-pierced at the corrected position.
            </p>
            <p>
              Earlobe damage does not heal on its own. A torn or split earlobe cannot close without surgical intervention because the skin at the tear edges heals separately, not together, leaving the split open or the hole permanently enlarged. Surgery repairs the defect by freshening the wound edges and closing them in layers with fine sutures, creating a healed earlobe with minimal scarring that is structurally sound enough to support earrings again after sufficient healing time.
            </p>
            <p>
              Dr. Vicky Jain performs earlobe repair surgery for partial tears, complete splits, enlarged holes from long-term earring use, and stretched earlobes from gauging. Both ears can be repaired in the same session. The repair technique is selected based on the type and extent of the deformity.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function QuickAnswersAlert() {
  return (
    <section className="bg-warm-white pb-10 md:pb-14" data-testid="quick-answers-alert" aria-labelledby="earlobe-quick-q">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-4xl py-6 px-4 md:px-6">
          <Info className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle id="earlobe-quick-q" className="font-serif text-lg text-premium-purple pl-7 mb-4">
            Can it be fixed — and when can you wear earrings again?
          </AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-4">
            <p className="leading-relaxed">
              <strong className="text-deep-navy font-medium">Yes, it can be fixed.</strong> Splits and stretched holes do not close reliably on their own; lobuloplasty restores a strong, natural earlobe contour in one short clinic visit.
            </p>
            <p className="leading-relaxed">
              <strong className="text-deep-navy font-medium">How:</strong> The torn edges are freshened (healed lining removed), then closed in layers with fine sutures so the repair holds — not just surface approximation.
            </p>
            <p className="leading-relaxed m-0">
              <strong className="text-deep-navy font-medium">Earrings:</strong> Re-piercing is typically safe at <strong className="font-medium text-deep-navy">6 to 8 weeks</strong>, at a new site with good tissue support — not through the old tear line. Details below.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Earlobe Repair</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The most important technical element of earlobe repair is freshening the wound edges before closure. Torn earlobe skin that has been open for more than a few days develops a healed epithelial lining along the tear edges. Simply suturing these healed edges together produces a repair that looks closed but is structurally weak and prone to re-tearing because the healed skin does not bond to itself in the same way fresh wound edges do. The epithelial lining is removed from the tear edges before closure, creating fresh wound surfaces that heal together as a genuine repair rather than a surface approximation.
              </p>
              <p>
                For complex deformities including significantly stretched earlobes from gauging, ears with multiple piercing sites creating an irregular border, and cases where simple linear closure would create an unnatural contour, Dr. Jain uses Z-plasty or other tissue rearrangement techniques. These techniques reorient the scar line to prevent contracture and produce a more natural earlobe shape than simple linear closure achieves. The technique is selected based on the specific deformity during the consultation examination.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const DEFORMITY_CARDS = [
  {
    title: "Partial Tear",
    copy: "The piercing hole has elongated downward but has not completely split through the lower earlobe margin. The hole appears as a keyhole shape rather than a round piercing. Repair removes the elongated channel and closes the defect, restoring a normal earlobe with a smaller, correctly positioned hole.",
  },
  {
    title: "Complete Split Earlobe",
    copy: "The earlobe has torn completely through the lower margin, dividing it into two separate pieces of tissue. Usually caused by a sudden pull on a heavy earring or earring caught on clothing. Repair freshens the edges and closes the two halves in layers to restore the earlobe as a single continuous structure.",
  },
  {
    title: "Enlarged Piercing Hole",
    copy: "The piercing hole has stretched over time from repeated earring use, the weight of heavy earrings, or aging-related tissue laxity. Not yet a split but large enough to be cosmetically visible and at risk of further tearing with continued earring wear. Repair excises the excess skin around the hole and closes it to a normal size.",
  },
  {
    title: "Stretched Earlobe from Gauging",
    copy: "Intentional stretching of the earlobe using progressively larger gauge plugs creates a large rounded hole in the earlobe. The skin is thin and the defect can be extensive depending on the final gauge size used. Repair requires more tissue reconstruction than standard split repair and uses techniques including local flap advancement to restore earlobe volume and shape.",
  },
  {
    title: "Multiple Piercing Deformity",
    copy: "Multiple old piercing sites that have enlarged, merged, or created an irregular earlobe contour. The cumulative effect of multiple piercings on earlobe tissue can distort the normal shape significantly. Repair reconstructs the earlobe contour while addressing each site.",
  },
  {
    title: "Traumatic Earlobe Injury",
    copy: "Sudden trauma including earring caught on clothing or jewelry, assault, or accidents causing complete or partial earlobe avulsion. Fresh traumatic tears are repaired as soon as possible to optimize healing. Older traumatic tears require edge freshening before closure as the healed skin cannot bond directly.",
  },
];

function TypesSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Earlobe Damage Requiring Repair</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {DEFORMITY_CARDS.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TECHNIQUE_CARDS = [
  {
    title: "Simple Linear Closure",
    copy: "The most commonly used technique for straightforward split earlobes. The healed skin along the tear edges is freshened by removing a thin strip of epithelium. The wound edges are then approximated in layers, closing the deep tissue first to provide structural support and the skin surface second. Fine sutures produce a narrow, well-healed scar that fades significantly over 6 to 12 months.",
  },
  {
    title: "Layered Closure",
    copy: "A reinforced version of linear closure that uses multiple suture layers to provide greater structural strength. Appropriate for complete splits and larger defects where a single-layer closure would be under more tension. The additional deep layers distribute the healing tension across the full thickness of the repair rather than concentrating it at the skin surface.",
  },
  {
    title: "Z-Plasty",
    copy: "A technique that makes two opposing diagonal incisions to create a Z-shaped pattern, then transposes the skin flaps to reorient the final scar line. Used for stretched or elongated earlobes where a simple linear closure would produce a notched or contracted scar. Z-plasty allows the repair to follow a more natural curved contour and distributes tension away from the scar line.",
  },
  {
    title: "Gauge Repair (Large Defect Reconstruction)",
    copy: "Large gauge holes that have stretched the earlobe to an extreme degree require a more complex reconstruction. The stretched skin is advanced and folded to rebuild earlobe volume. Techniques are individualized to the specific size and shape of the gauge defect.",
  },
];

function TechniquesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Earlobe Repair Techniques</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {TECHNIQUE_CARDS.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Earlobe Repair Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 20 to 40 minutes under local anesthesia. No hospital admission is required. Both ears can be repaired in the same session if both are affected. Dr. Jain marks the planned repair and discusses the approach with the patient before administering local anesthetic to the earlobe.
            </p>
            <p>
              After anesthesia, the epithelial lining along the tear edges is carefully removed to create fresh wound surfaces. The repair is closed in layers using fine absorbable sutures for the deeper tissue and fine non-absorbable sutures for the skin. Skin sutures are removed at 7 to 10 days. A small dressing is applied after the procedure.
            </p>
            <p>
              Patients leave the clinic immediately after surgery and return to normal activity the same day in most cases. The earlobe is kept clean and dry during the healing period. Suture removal at 7 to 10 days is the only post-operative visit required in straightforward cases.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and tenderness at the repair site. Normal and expected. The earlobe is kept clean and dry. A small dressing covers the sutures.",
      icon: Sparkles,
    },
    {
      period: "Days 7 to 10",
      description:
        "Sutures are removed. The repair site is healing cleanly. The earlobe appears close to its natural shape. Mild pinkness at the repair line is normal.",
      icon: Stethoscope,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Complete healing for most patients. Normal activity with no restriction. The scar line is visible but fading.",
      icon: CheckCircle2,
    },
    {
      period: "Weeks 6 to 8",
      description:
        "The healed earlobe is sufficiently strong for re-piercing at the corrected position. The piercing should be placed at a new site with adequate tissue support rather than at the previous tear location.",
      icon: Monitor,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function RepiercingSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="repiercing-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">After Surgery</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">When Can You Re-Pierce After Earlobe Repair?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Re-piercing is appropriate 6 to 8 weeks after earlobe repair once the repair has fully healed and the tissue has regained adequate structural strength to support a new piercing. The new piercing should be placed at a different site from the previous tear, with sufficient tissue margin on all sides to prevent a repeat tear at the same location. Piercing too close to the repair site places the suture line under the same stress that caused the original tear.
            </p>
            <p>
              After re-piercing, wearing lightweight earrings initially and avoiding very heavy earrings permanently reduces the risk of a future tear. Earrings should be removed before sleeping to prevent accidental pulls on the earring during sleep. Earring support patches applied behind the earlobe distribute the weight of the earring over a larger area of tissue, reducing the concentrated stress on the piercing hole that causes progressive elongation over time.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Consider Earlobe Repair?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any adult with a torn, split, elongated, or stretched earlobe that is causing cosmetic concern or preventing comfortable earring wear is appropriate for consultation. There is no minimum severity. A partial tear that has not yet completely split is a valid indication for early repair. Waiting until the earlobe tears completely results in a more extensive repair and potentially a longer recovery than addressing the elongating hole before it splits through.
            </p>
            <p>
              Children with torn earlobes from earring injuries are also appropriate candidates for earlobe repair. The procedure is performed under local anesthesia and is well-tolerated. There is no lower age limit for repair. Patients with large gauge stretching who have removed their jewelry and want their earlobes restored to a conventional appearance are candidates for the more complex gauge repair reconstruction, which Dr. Jain assesses individually based on the size of the defect.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {EARLOBE_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`earlobe-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Earlobe Repair Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines the earlobe defect, assesses the type and extent of the damage, and selects the repair technique appropriate for your specific deformity. Both ears are assessed together and both can be repaired in the same session. You leave with a clear plan and a realistic picture of the healing timeline and when re-piercing is appropriate.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Otoplasty",
      description: "Ear reshaping surgery for patients with protruding or asymmetric ears alongside earlobe concerns.",
      href: OTOPLASTY_PATH,
    },
    {
      name: "Mole Removal",
      description: "Assessment and removal of skin lesions on the ear alongside earlobe repair.",
      href: MOLE_PATH,
    },
    {
      name: "Keloid Treatment",
      description: "Management of keloid scars that form at piercing sites or after earlobe repair in predisposed patients.",
      href: KELOID_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function EarlobeRepairPage() {
  return (
    <>
      <SEO
        title="Earlobe Repair Surgery | Dr. Vicky Jain"
        description="Torn, split, or stretched earlobes from earrings, trauma, or gauging. Dr. Vicky Jain performs earlobe repair surgery with minimal scarring. Book today."
        canonical={PAGE_PATH}
      />
      <EarlobeJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <QuickAnswersAlert />
      <DrJainApproachSection />
      <TypesSection />
      <TechniquesSection />
      <HowPerformedSection />
      <RecoveryTimelineSection />
      <RepiercingSection />
      <GoodCandidateSection />
      <FAQSection />
      <PatientTestimonials procedureName="Otoplasty" />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
